import {
    RiDeleteBinLine, RiEdit2Line,
    RiInputField,
    RiListCheck,
} from "@remixicon/react";
import React, {
    ReactNode,
    useMemo,
} from "react";
import {
    GetChannelTagsDocument,
    GetSettingsDocument,
    useGetChannelSettingsQuery,
    useSaveSettingsMutation,
} from "../../../../../__generated__/api";
import useRouting from "../../../../../contexts/Routing";

import useDeleteConfirm from "../../../../../components/confirm/Delete";
import Badge from "../../../../../components/theme/badge/Badge";
import SettingsContainer from "../../../../../components/theme/settings";
import createTableContainer from "../../../../../components/theme/table";
import { SettingsProps } from "../../base";

interface ChannelTag {
    id: string;
    tag: string;
    channelCount: number;
}
const TableContainer = createTableContainer<ChannelTag>();

export default function ChannelTagsTable(props: SettingsProps): ReactNode {
    const [save] = useSaveSettingsMutation();

    const { element: deleteElement, setDeleting } = useDeleteConfirm<ChannelTag>(
        async item => {
            const channelTags = props.settings?.channels?.channelTags?.filter(current => current !== item.id) ?? [];

            await save({
                refetchQueries: [
                    { query: GetChannelTagsDocument },
                    { query: GetSettingsDocument },
                ],
                variables: {
                    channels: {
                        channelTags,
                    },
                },
            });
        },
        item => item.tag,
        "Remove Channel Group",
        "Once a channel group has been removed, any automations or channel "
            + "settings associated with the channel group will be deactivated.",
        "Remove",
    );

    const { data: chatSettings, loading: isLoading } = useGetChannelSettingsQuery();

    const tags = useMemo(() => {
        const tagMap = chatSettings?.channelTags?.toIdMap(row => row.options?.tag?.name ?? "") ?? {};
        return props.settings?.channels?.channelTags?.map(tag => {
            const settings = tagMap[tag];
            return {
                id: settings?.id ?? `urn:fq:channel-tag:${tag}`,
                tag,
                channelCount: settings?.options?.tag?.channelCount ?? 0,
            };
        }) ?? [];
    }, [props.settings, chatSettings]);

    const { Paths, createHistoryPush } = useRouting();

    return (
        <>
            <SettingsContainer navigation="none">
                <SettingsContainer.Section title="Channel Groups" shouldDisplayTitle={false}>
                    <SettingsContainer.Setting
                        title="Channel Groups"
                        description="Custom fields allow you to store and request information from users in a structured way.
                    By creating a custom field and adding it to a request type, any user creating a ticket with that
                    request type is required to fill in the custom field based on the type of field. If the custom
                    field is just text, the user will be required to enter text. If the field is a dropdown, the user
                    will be required to select from a set of provided options."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isFullWidth
                    >
                        <TableContainer data={tags} isLoading={isLoading || props.isLoading}>
                            <TableContainer.Empty>
                                <TableContainer.Empty.Default
                                    text="Channel groups are a versatile way to organize your channels. By creating a
                                channel group, you are able to apply settings to a group of channels,
                                apply automations, send messages in bulk, and many other features."
                                    title="Create an Channel Group"
                                    icon={<RiInputField />}
                                    onClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                        setting: "channel-groups",
                                        id: "new",
                                    })}
                                    buttonText="Add Channel Group"
                                />
                            </TableContainer.Empty>
                            <TableContainer.TableControls>

                                <TableContainer.TableControls.Search
                                    search={(query, item) => item.tag?.toLocaleLowerCase().includes(query)}
                                />
                                <TableContainer.TableControls.Seperator />

                                <TableContainer.TableControls.SortBy />
                                <TableContainer.TableControls.Create
                                    text="Add Channel Group"
                                    onClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                        setting: "channel-groups",
                                        id: "new",
                                    })}
                                />
                            </TableContainer.TableControls>
                            <TableContainer.Table>
                                <TableContainer.TableValue
                                    columnKey="name"
                                    title="Name"
                                    sortBy={item => item.tag?.toLowerCase()}
                                >
                                    {item => item.tag}
                                </TableContainer.TableValue>

                                <TableContainer.TableValue
                                    columnKey="channelCount"
                                    title="Channels"
                                    sortBy={item => item.channelCount}
                                >
                                    {item => (
                                        <Badge variant="gray" prefixIcon={<RiListCheck />}>
                                            {item.channelCount}
                                        </Badge>
                                    )}
                                </TableContainer.TableValue>

                                <TableContainer.Action
                                    icon={<RiEdit2Line />}
                                    altText="Edit Channel Group"
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    getLinkParams={item => ({
                                        setting: "channel-groups",
                                        id: item.id,
                                    })}
                                />
                                <TableContainer.Action
                                    icon={<RiDeleteBinLine />}
                                    altText="Delete Channel Tag"
                                    onClick={setDeleting}
                                />

                            </TableContainer.Table>
                        </TableContainer>
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>
            </SettingsContainer>
            {deleteElement}
        </>
    );
}
